
import { neon } from '@neondatabase/serverless';
import { Profile, PixTransaction, SystemStats } from './types';

// String de conexão fornecida
const CONNECTION_STRING = "postgresql://neondb_owner:npg_Sk5dMnhlG6DJ@ep-empty-cherry-ac8115rr-pooler.sa-east-1.aws.neon.tech/neondb?sslmode=require";

let sqlInstance: any = null;

const getSql = () => {
  if (sqlInstance) return sqlInstance;
  try {
    const url = (process.env.DATABASE_URL && process.env.DATABASE_URL !== 'undefined') 
      ? process.env.DATABASE_URL 
      : CONNECTION_STRING;
    
    sqlInstance = neon(url);
    return sqlInstance;
  } catch (e) {
    console.error("Erro ao configurar cliente Neon:", e);
    return null;
  }
};

// Inicializa as tabelas se não existirem
export const initDb = async () => {
  const sql = getSql();
  if (!sql) return;
  try {
    // Tenta habilitar extensões comuns de UUID
    try {
      await sql`CREATE EXTENSION IF NOT EXISTS "pgcrypto"`;
    } catch (extErr) {
      console.warn("Aviso: Falha ao carregar pgcrypto (pode já estar ativo ou sem permissão)");
    }

    await sql`
      CREATE TABLE IF NOT EXISTS profiles (
        id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
        username TEXT UNIQUE NOT NULL,
        display_name TEXT NOT NULL,
        pix_key TEXT NOT NULL,
        avatar_url TEXT,
        bio TEXT,
        password TEXT,
        status TEXT DEFAULT 'active',
        provider TEXT DEFAULT 'credentials',
        created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
      );
    `;

    await sql`
      CREATE TABLE IF NOT EXISTS transactions (
        id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
        sender_id TEXT NOT NULL,
        sender_name TEXT NOT NULL,
        sender_avatar_url TEXT,
        receiver_id TEXT NOT NULL,
        receiver_name TEXT NOT NULL,
        receiver_avatar_url TEXT,
        amount DECIMAL(12,2) NOT NULL,
        message TEXT,
        has_reward BOOLEAN DEFAULT FALSE,
        is_public BOOLEAN DEFAULT TRUE,
        created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
      );
    `;
    console.log("Banco de dados sincronizado com sucesso.");
  } catch (e) {
    console.error("Erro crítico na sincronização do banco:", e);
  }
};

export const db = {
  isConfigured: () => getSql() !== null,

  getLatestTransactions: async (): Promise<PixTransaction[]> => {
    const sql = getSql();
    if (!sql) return [];
    try {
      const rows = await sql`SELECT * FROM transactions WHERE is_public = true ORDER BY created_at DESC LIMIT 30`;
      return rows.map((r: any) => ({
        id: r.id, 
        sender_id: r.sender_id, 
        sender_name: r.sender_name,
        sender_avatar_url: r.sender_avatar_url,
        receiver_id: r.receiver_id, 
        receiver_name: r.receiver_name,
        receiver_avatar_url: r.receiver_avatar_url,
        amount: Number(r.amount), 
        message: r.message, 
        created_at: r.created_at,
        is_public: r.is_public, 
        has_reward: r.has_reward
      }));
    } catch (e) { 
      console.error("Erro ao carregar transações:", e);
      return []; 
    }
  },

  getProfile: async (username: string): Promise<Profile | null> => {
    const sql = getSql();
    if (!sql) return null;
    try {
      const rows = await sql`SELECT * FROM profiles WHERE username = ${username.toLowerCase().trim()} LIMIT 1`;
      return rows.length > 0 ? (rows[0] as Profile) : null;
    } catch (e) { 
      console.error("Erro ao buscar perfil:", e);
      return null; 
    }
  },

  searchProfiles: async (query: string): Promise<Profile[]> => {
    const sql = getSql();
    if (!sql || !query || query.length < 2) return [];
    try {
      const searchTerm = `%${query.toLowerCase()}%`;
      const rows = await sql`
        SELECT * FROM profiles 
        WHERE LOWER(username) LIKE ${searchTerm} 
        OR LOWER(display_name) LIKE ${searchTerm} 
        LIMIT 5
      `;
      return rows as Profile[];
    } catch (e) {
      console.error("Erro ao buscar perfis:", e);
      return [];
    }
  },

  registerUser: async (data: Partial<Profile>): Promise<Profile | null> => {
    const sql = getSql();
    if (!sql) return null;
    try {
      const rows = await sql`
        INSERT INTO profiles (username, display_name, pix_key, password, avatar_url, bio)
        VALUES (${data.username?.toLowerCase()}, ${data.display_name}, ${data.pix_key}, ${data.password}, ${data.avatar_url}, ${data.bio || ''})
        RETURNING *
      `;
      return rows[0] as Profile;
    } catch (e) {
      console.error("Erro ao registrar usuário:", e);
      return null;
    }
  },

  updateAvatar: async (userId: string, avatarUrl: string): Promise<boolean> => {
    const sql = getSql();
    if (!sql) return false;
    try {
      await sql`
        UPDATE profiles 
        SET avatar_url = ${avatarUrl} 
        WHERE id = ${userId}
      `;
      return true;
    } catch (e) {
      console.error("Erro ao atualizar avatar:", e);
      return false;
    }
  },

  createTransaction: async (tx: any) => {
    const sql = getSql();
    if (!sql) {
      console.error("SQL não inicializado.");
      return false;
    }
    try {
      const numericAmount = parseFloat(tx.amount.toString());
      
      await sql`
        INSERT INTO transactions (
          sender_id, sender_name, sender_avatar_url, 
          receiver_id, receiver_name, receiver_avatar_url, 
          amount, message, has_reward, is_public
        )
        VALUES (
          ${tx.sender_id.toString()}, 
          ${tx.sender_name}, 
          ${tx.sender_avatar_url}, 
          ${tx.receiver_id.toString()}, 
          ${tx.receiver_name}, 
          ${tx.receiver_avatar_url}, 
          ${numericAmount}, 
          ${tx.message}, 
          ${tx.has_reward ?? false}, 
          ${tx.is_public ?? true}
        )
      `;
      return true;
    } catch (e: any) { 
      console.error("Erro SQL ao criar transação:", e.message || e);
      return false; 
    }
  }
};
